
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { RPKTypeT } from '@/core/types/rpk.types'
import { CarrierT } from '@/core/types/carriers.types'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'
import { rpk } from '@/core/realisations/rpk.realisation'

// constants
import { STATUSES_RPK_RPP } from '@/core/constants/orders.constants'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['reset', 'search'],
    setup(props, { emit }) {
        const state = reactive({
            id: undefined as number | undefined,
            ftl_order_id: undefined as number | undefined,
            turnover_date_from: undefined as string | undefined,
            turnover_date_to: undefined as string | undefined,
            type: undefined as number | undefined,
            company_id: undefined as number | undefined,
            act_number: undefined as string | undefined,
            act_date_from: undefined as string | undefined,
            act_date_to: undefined as string | undefined,
            created_at_from: undefined as string | undefined,
            status_id: undefined as number | undefined,
            pbc_date_from: undefined as string | undefined,
            pbc_date_to: undefined as string | undefined,
        })

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            types: new DataState<RPKTypeT>({}),
        })

        const reset = () => {
            state.id = undefined
            state.ftl_order_id = undefined
            state.turnover_date_from = undefined
            state.turnover_date_to = undefined
            state.type = undefined
            state.company_id = undefined
            state.act_number = undefined
            state.act_date_from = undefined
            state.act_date_to = undefined
            state.created_at_from = undefined
            state.status_id = undefined
            state.pbc_date_from = undefined
            state.pbc_date_to = undefined

            emit('reset')
        }

        const search = () => {
            emit('search', {
                id: state.id,
                ftl_order_id: state.ftl_order_id,
                turnover_date_from: state.turnover_date_from,
                turnover_date_to: state.turnover_date_to,
                type: state.type,
                company_id: state.company_id,
                act_number: state.act_number,
                act_date_from: state.act_date_from,
                act_date_to: state.act_date_to,
                created_at_from: state.created_at_from,
                status_id: state.status_id,
                pbc_date_from: state.pbc_date_from,
                pbc_date_to: state.pbc_date_to
            })
        }

        // types

        const loadTypes = async () => {
            try {
                data_state.types.is_loading = true
                const response = await rpk.getTypes()
                data_state.types.list = response.data

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        // companies

        const loadCompanies = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, statusId: 2 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CarrierT) => {
            await loadCompanies(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(company)
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadTypes()
        })

        return {
            state,
            data_state,
            // types
            loadTypes,
            // companies
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            // form
            reset,
            search,
            // constants
            STATUSES_RPK_RPP,
        }
    },
})
